import React from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { useQuery } from '@apollo/client';
import { GET_MY_PROFILE } from '../../graphql/operations';
import { UPLOAD_BASE_URL } from '../../config';

interface SavedDesignsModalProps {
  show: boolean;
  onHide: () => void;
  onSelectDesign: (fileName: string, friendlyName: string) => void;
}

const SavedDesignsModal: React.FC<SavedDesignsModalProps> = ({ show, onHide, onSelectDesign }) => {
  const { data, loading, error } = useQuery(GET_MY_PROFILE, {
    fetchPolicy: 'cache-first'
  });

  const savedDesigns = data?.myProfile?.savedDesigns || [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold fs-5">
          <i className="fas fa-images text-primary me-2"></i> Choose from Saved Designs
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        {loading ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-spinner fa-spin fa-2x mb-3"></i>
            <p>Loading your designs...</p>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <i className="fas fa-exclamation-triangle mb-2" style={{ fontSize: '24px' }}></i>
            <p>Error loading designs.</p>
          </div>
        ) : savedDesigns.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-folder-open mb-3" style={{ fontSize: '32px', opacity: 0.5 }}></i>
            <p className="mb-0">You don't have any saved designs yet.</p>
            <p className="small">Upload designs in your Profile to use them here.</p>
          </div>
        ) : (
          <Row className="g-3">
            {savedDesigns.map((design: any) => (
              <Col xs={12} sm={6} md={4} key={design.id}>
                <div 
                  className="border rounded-3 p-2 bg-white h-100 d-flex flex-column shadow-sm"
                  style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                  onClick={() => onSelectDesign(design.fileName, design.friendlyName)}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)'; }}
                >
                  <div className="text-center mb-2 flex-grow-1 d-flex align-items-center justify-content-center bg-light rounded border overflow-hidden" style={{ minHeight: '120px' }}>
                    {design.fileName.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                      <img src={`${UPLOAD_BASE_URL}/${design.fileName}`} alt={design.friendlyName} style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }} />
                    ) : (
                      <div className="text-center py-2">
                        <i className="fas fa-file-alt text-secondary mb-1" style={{ fontSize: '32px' }}></i>
                        <div className="text-muted" style={{fontSize: '10px'}}>{design.fileName.split('.').pop()?.toUpperCase()}</div>
                      </div>
                    )}
                  </div>
                  <div className="text-truncate fw-semibold text-dark small text-center" title={design.friendlyName}>
                    {design.friendlyName}
                  </div>
                  <div className="text-muted text-center" style={{ fontSize: '10px' }}>
                    {new Date(Number(design.uploadedAt)).toLocaleDateString()}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SavedDesignsModal;
